/**
 * COLPSignoffForm — drawer-embedded COLP terminal-decision form (Brief 14c-iii).
 *
 * 4 outcomes (approve / approve_with_conditions / reject / remand_to_mlco).
 * Conditions textarea visible+required only when outcome=approve_with_conditions.
 * Remand-reason textarea visible+required only when outcome=remand_to_mlco.
 * 60s debounce via useRef-backed timestamp prevents double-submit; 5s
 * short-retry window on failure (same shape as MLCOReviewForm).
 *
 * Role gate: only renders for is_colp.
 *
 * Structurally parallel to MLCOReviewForm.jsx (Brief 14c-ii) per brief
 * §10 #1 — intentionally not abstracted into a shared DecisionForm.
 */
import React, { useCallback, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { API } from "../../config/api";

import { errorMessage } from "../../utils/errorMessage";
const OUTCOMES = [
  {
    value: "approve",
    label: "Approve",
    description: "COLP signs off; matter proceeds.",
  },
  {
    value: "approve_with_conditions",
    label: "Approve with conditions",
    description: "Acceptable if specific safeguards are followed.",
  },
  {
    value: "reject",
    label: "Reject",
    description: "COLP refuses; matter cannot proceed.",
  },
  {
    value: "remand_to_mlco",
    label: "Remand to MLCO",
    description: "Send back to MLCO with a reason; not terminal.",
  },
];

const DEBOUNCE_MS = 60_000;
const SHORT_RETRY_MS = 5_000;
const MAX_TEXT = 4000;

function authHeaders() {
  const email =
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") ||
    "";
  return email
    ? { "X-Bonsai-User-Id": email }
    : {};
}

export default function COLPSignoffForm({ reviewId, userMeta, onDecided, onCancel }) {
  const [outcome, setOutcome] = useState("");
  const [rationale, setRationale] = useState("");
  const [conditions, setConditions] = useState("");
  const [remandReason, setRemandReason] = useState("");
  const [busy, setBusy] = useState(false);
  const lastSubmitRef = useRef(0);
  const callerEmail =
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") ||
    "";

  const conditionsRequired = outcome === "approve_with_conditions";
  const remandRequired = outcome === "remand_to_mlco";
  const canShow = !!userMeta?.is_colp;

  const submit = useCallback(async () => {
    const now = Date.now();
    if (now - lastSubmitRef.current < DEBOUNCE_MS) {
      toast.error("Please wait a moment before resubmitting.");
      return;
    }
    if (!outcome) {
      toast.error("Choose an outcome.");
      return;
    }
    if (!rationale.trim()) {
      toast.error("Rationale is required.");
      return;
    }
    if (conditionsRequired && !conditions.trim()) {
      toast.error("Conditions are required for ‘Approve with conditions’.");
      return;
    }
    if (remandRequired && !remandReason.trim()) {
      toast.error("Remand reason is required for ‘Remand to MLCO’.");
      return;
    }
    if (!callerEmail) {
      toast.error("Cannot submit — missing user email.");
      return;
    }

    lastSubmitRef.current = now;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/compliance/matter-reviews/${reviewId}/colp-decision`,
        {
          outcome,
          rationale,
          conditions: conditionsRequired ? conditions : null,
          remand_reason: remandRequired ? remandReason : null,
          decided_by_email: callerEmail,
        },
        { headers: authHeaders() }
      );
      if (outcome === "remand_to_mlco") {
        toast.success("Remanded to MLCO");
      } else {
        toast.success("COLP decision recorded");
      }
      if (onDecided) onDecided(res.data);
    } catch (err) {
      console.error("COLP decision failed:", err);
      toast.error(
        errorMessage(err, "Could not save decision. Try again.")
      );
      lastSubmitRef.current = Date.now() - (DEBOUNCE_MS - SHORT_RETRY_MS);
    } finally {
      setBusy(false);
    }
  }, [
    outcome,
    rationale,
    conditions,
    remandReason,
    conditionsRequired,
    remandRequired,
    callerEmail,
    reviewId,
    onDecided,
  ]);

  if (!canShow) {
    return null;
  }

  return (
    <div className="space-y-3" data-testid="colp-signoff-form">
      <fieldset
        className="space-y-2"
        role="radiogroup"
        aria-label="COLP decision outcome"
      >
        {OUTCOMES.map((opt) => {
          const descId = `colp-outcome-${opt.value}-desc`;
          return (
            <label
              key={opt.value}
              className="flex items-start gap-2 text-sm text-slate-200 cursor-pointer"
              data-testid={`colp-outcome-${opt.value}-label`}
            >
              <input
                type="radio"
                name="colp-outcome"
                value={opt.value}
                checked={outcome === opt.value}
                onChange={() => setOutcome(opt.value)}
                disabled={busy}
                aria-describedby={descId}
                data-testid={`colp-outcome-${opt.value}-radio`}
                className="mt-1"
              />
              <span>
                <span className="font-medium">{opt.label}</span>
                <span
                  id={descId}
                  className="block text-xs text-slate-400 mt-0.5"
                >
                  {opt.description}
                </span>
              </span>
            </label>
          );
        })}
      </fieldset>

      <div>
        <label
          htmlFor="colp-rationale"
          className="block text-xs font-medium text-slate-300 mb-1"
        >
          Rationale <span className="text-red-400">*</span>
        </label>
        <textarea
          id="colp-rationale"
          rows={4}
          maxLength={MAX_TEXT}
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          disabled={busy}
          placeholder="Explain your decision…"
          className="w-full bg-slate-900/60 border border-slate-700/60 rounded-md p-2 text-sm text-slate-200 placeholder-slate-500"
          data-testid="colp-rationale-textarea"
        />
        <div className="text-xs text-slate-500 text-right mt-0.5" data-testid="colp-rationale-counter">
          {rationale.length} / {MAX_TEXT}
        </div>
      </div>

      {conditionsRequired && (
        <div data-testid="colp-conditions-wrapper">
          <label
            htmlFor="colp-conditions"
            className="block text-xs font-medium text-slate-300 mb-1"
          >
            Conditions <span className="text-red-400">*</span>
          </label>
          <textarea
            id="colp-conditions"
            rows={3}
            maxLength={MAX_TEXT}
            value={conditions}
            onChange={(e) => setConditions(e.target.value)}
            disabled={busy}
            placeholder="State the conditions…"
            className="w-full bg-slate-900/60 border border-slate-700/60 rounded-md p-2 text-sm text-slate-200 placeholder-slate-500"
            data-testid="colp-conditions-textarea"
          />
        </div>
      )}

      {remandRequired && (
        <div data-testid="colp-remand-wrapper">
          <label
            htmlFor="colp-remand-reason"
            className="block text-xs font-medium text-slate-300 mb-1"
          >
            Reason for remand <span className="text-red-400">*</span>
          </label>
          <textarea
            id="colp-remand-reason"
            rows={3}
            maxLength={MAX_TEXT}
            value={remandReason}
            onChange={(e) => setRemandReason(e.target.value)}
            disabled={busy}
            placeholder="Explain what MLCO needs to revisit…"
            className="w-full bg-slate-900/60 border border-slate-700/60 rounded-md p-2 text-sm text-slate-200 placeholder-slate-500"
            data-testid="colp-remand-reason-textarea"
          />
        </div>
      )}

      <div className="flex items-center gap-2 pt-2">
        <button
          type="button"
          disabled={busy}
          onClick={submit}
          className="px-3 py-1.5 rounded-md text-xs font-medium bg-purple-700/40 text-purple-100 border border-purple-700/60 hover:bg-purple-700/60 disabled:opacity-50"
          data-testid="colp-decision-submit-btn"
        >
          {busy ? (
            <span className="inline-flex items-center gap-1">
              <Loader2 size={12} className="animate-spin" />
              Saving…
            </span>
          ) : (
            "Save decision"
          )}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onCancel}
          className="px-3 py-1.5 rounded-md text-xs font-medium bg-slate-700 text-slate-200 border border-slate-600 hover:bg-slate-600 disabled:opacity-50"
          data-testid="colp-decision-cancel-btn"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
